import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEmail, useDeleteEmail } from '../../hooks/useEmails';
import { useSpaceId } from '../../contexts/SpaceContext';
import { routes } from '../../lib/routes';
import { Badge, Button, Card, CardHeader, ConfirmModal } from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import { Copy } from 'lucide-react';

export default function EmailDetail() {
  const { emailId } = useParams<{ emailId: string }>();
  const navigate = useNavigate();
  const spaceId = useSpaceId();
  const { success, error: showError } = useToast();

  const { data: email, isLoading, error } = useEmail(emailId);
  const deleteEmail = useDeleteEmail();

  const [deleteModal, setDeleteModal] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !email) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load email address</p>
        <Link to={routes.console.spaceConnectionEmails(spaceId)} className="text-primary-600 hover:text-primary-500 mt-2 inline-block">
          Back to emails
        </Link>
      </div>
    );
  }

  const handleDelete = async () => {
    try {
      await deleteEmail.mutateAsync(email.emailId);
      success('Email address deleted successfully');
      navigate(routes.console.spaceConnectionEmails(spaceId));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete email address');
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(email.emailAddress);
      success('Email address copied to clipboard');
    } catch {
      showError('Failed to copy to clipboard');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{email.emailAddress}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Email address for knowledge base ingestion
          </p>
        </div>
        <div className="flex gap-2">
          <Link to={routes.console.spaceConnectionEmailEdit(spaceId, email.emailId)}>
            <Button>Edit</Button>
          </Link>
          <Button variant="danger" onClick={() => setDeleteModal(true)}>
            Delete
          </Button>
        </div>
      </div>

      {/* Email address card */}
      <Card>
        <CardHeader title="Email Address" />
        <div className="flex items-center gap-3">
          <code className="text-sm bg-muted px-3 py-2 rounded-md flex-1 font-mono">
            {email.emailAddress}
          </code>
          <Button variant="ghost" size="sm" onClick={handleCopy}>
            <Copy className="h-4 w-4" />
          </Button>
        </div>
      </Card>

      {/* Allowed senders card */}
      <Card>
        <CardHeader
          title="Allowed Senders"
          description={
            email.allowedSenders.length > 0
              ? 'Only these email addresses can send to this inbox'
              : 'All senders are allowed to send to this inbox'
          }
        />
        {email.allowedSenders.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {email.allowedSenders.map((sender) => (
              <Badge key={sender} variant="gray">
                {sender}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No sender restrictions — all incoming emails will be accepted.
          </p>
        )}
      </Card>

      {/* Metadata */}
      <Card>
        <CardHeader title="Metadata" />
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Email ID</dt>
            <dd className="mt-1 text-sm text-foreground font-mono">{email.emailId}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Created</dt>
            <dd className="mt-1 text-sm text-foreground">
              {new Date(email.createdAt).toLocaleString()}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Updated</dt>
            <dd className="mt-1 text-sm text-foreground">
              {new Date(email.updatedAt).toLocaleString()}
            </dd>
          </div>
        </dl>
      </Card>

      {/* Danger zone */}
      <Card className="border-red-200 dark:border-red-800">
        <CardHeader
          title="Danger Zone"
          description="Irreversible and destructive actions"
        />
        <Button variant="danger" onClick={() => setDeleteModal(true)}>
          Delete Email Address
        </Button>
      </Card>

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Email Address"
        message={`Are you sure you want to delete "${email.emailAddress}"? Any knowledge base ingestion from this address will stop. This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteEmail.isPending}
      />
    </div>
  );
}
